import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Page } from '@/components/Page';
import { Section } from '@/components/Section';
import { Spinner } from '@/components/Spinner';
import { Empty } from '@/components/Empty';
import { sb } from '@/hooks/useSupabaseQuery';
import { resolveEtaScenarios } from '@/lib/etaScenarios';
import type { EtaAlert, ScenarioResult } from '@/types/domain';

function fmtPrice(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  if (v >= 1000) return v.toLocaleString('en-US', { maximumFractionDigits: 0 });
  if (v >= 1) return v.toLocaleString('en-US', { maximumFractionDigits: 2 });
  return v.toLocaleString('en-US', { maximumFractionDigits: 4 });
}

function fmtUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `$${v.toFixed(2)}`;
}

function fmtSignedUsd(v: number | null | undefined): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v > 0 ? '+' : ''}$${v.toFixed(2)}`;
}

function fmtPct(v: number | null | undefined, digits = 1): string {
  if (v == null || !Number.isFinite(v)) return '—';
  return `${v.toFixed(digits)}%`;
}

function fmtBase(qty: number | null | undefined, base: string): string {
  if (qty == null || !Number.isFinite(qty) || qty <= 0) return '—';
  return `${qty.toLocaleString('en-US', { maximumSignificantDigits: 4, minimumSignificantDigits: 1, useGrouping: false })} ${base}`;
}

function fmtExpiry(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString('ru', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch {
    return iso;
  }
}

function extractBase(normalizedKey: string): string {
  const parts = normalizedKey.split('-');
  const baseRaw = parts[0] ?? '';
  return (baseRaw.includes('_') ? baseRaw.split('_')[0] : baseRaw).toUpperCase();
}

interface ScenarioCardProps {
  scenario: ScenarioResult;
  base: string;
  daysToExpiry: number;
  withShort: boolean;
}

function ScenarioCard({ scenario: s, base, daysToExpiry, withShort }: ScenarioCardProps): JSX.Element {
  const cell: React.CSSProperties = { padding: '6px 0', borderBottom: '0.5px solid var(--tg-separator)' };
  const label: React.CSSProperties = { color: 'var(--tg-hint)', fontSize: 12, marginBottom: 2 };
  const value: React.CSSProperties = { color: 'var(--tg-text)', fontSize: 14, fontWeight: 500 };

  const netColor = s.netUsd > 0 ? '#34c759' : s.netUsd < 0 ? '#ff3b30' : 'var(--tg-text)';
  const grossColor = s.grossUsd > 0 ? '#34c759' : s.grossUsd < 0 ? '#ff3b30' : 'var(--tg-text)';
  const buySizeText = fmtBase(s.buyAskSizeBase ?? s.qtyBase, base);
  const sellSizeText = fmtBase(s.sellBidSizeBase ?? s.qtyBase, base);
  const buySizeEstimated = s.buyAskSizeEstimated === true;
  const sellSizeEstimated = s.sellBidSizeEstimated === true;
  const requiredCapital = s.capitalRequiredUsd ?? (!withShort ? s.buyAsk * s.qtyBase : null);
  const estimatedCapital = s.capitalRequiredUsd == null && !withShort;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px 16px', marginBottom: 12 }}>
        <div>
          <div style={label}>Покупка (buy)</div>
          <div style={value}>{s.buyExch.toUpperCase()}</div>
          <div style={{ fontSize: 12, color: 'var(--tg-hint)' }}>
            ask {fmtPrice(s.buyAsk)} USDT × <span style={{ color: buySizeEstimated ? '#8e8e93' : 'inherit' }}>{buySizeEstimated ? `~${buySizeText}` : buySizeText}</span>
          </div>
        </div>
        <div>
          <div style={label}>{withShort ? 'Шорт (sell)' : 'Продажа (sell)'}</div>
          <div style={value}>{s.sellExch.toUpperCase()}</div>
          <div style={{ fontSize: 12, color: 'var(--tg-hint)' }}>
            bid {fmtPrice(s.sellBid)} USDT × <span style={{ color: sellSizeEstimated ? '#8e8e93' : 'inherit' }}>{sellSizeEstimated ? `~${sellSizeText}` : sellSizeText}</span>
          </div>
        </div>
        <div>
          <div style={label}>Объём</div>
          <div style={value}>{fmtBase(s.qtyBase, base)}</div>
        </div>
        <div>
          <div style={label}>Gross P&L</div>
          <div style={{ ...value, color: grossColor }}>{fmtSignedUsd(s.grossUsd)}</div>
        </div>
        <div>
          <div style={label}>Комиссии</div>
          <div style={{ ...value, color: '#ff3b30' }}>{fmtUsd(s.feesUsd)}</div>
        </div>
        <div>
          <div style={label}>Net P&L</div>
          <div style={{ ...value, color: netColor, fontSize: 16 }}>{fmtSignedUsd(s.netUsd)}</div>
        </div>
        <div>
          <div style={label}>ROI премии</div>
          <div style={value}>{fmtPct(s.netPctOfPremium)}</div>
        </div>
        {requiredCapital != null && (
          <div>
            <div style={label}>Требуемый капитал</div>
            <div style={value}>{fmtUsd(requiredCapital)}{estimatedCapital ? <span style={{ color: 'var(--tg-hint)', fontSize: 12 }}> (оценка)</span> : null}</div>
            {!withShort && (
              <div style={{ color: 'var(--tg-hint)', fontSize: 11 }}>
                Требуемый капитал = премия за покупку (лонг на sell-бирже уже открыт).
              </div>
            )}
          </div>
        )}
        {withShort && s.premiumLockUsd != null && (
          <>
            <div>
              <div style={label}>Premium lock</div>
              <div style={value}>{fmtUsd(s.premiumLockUsd)}</div>
            </div>
            <div>
              <div style={label}>Margin lock</div>
              <div style={value}>{fmtUsd(s.marginLockUsd)}</div>
            </div>
            <div>
              <div style={label}>Total capital</div>
              <div style={value}>{fmtUsd(s.totalCapitalUsd)}</div>
            </div>
            <div>
              <div style={label}>ROI капитала</div>
              <div style={value}>{fmtPct(s.roiOnCapitalPct)}</div>
            </div>
            <div>
              <div style={label}>APR</div>
              <div style={{ ...value, color: '#ff9500', fontSize: 16 }}>{fmtPct(s.aprPct, 0)}</div>
            </div>
            <div>
              <div style={label}>Liq. price</div>
              <div style={{ ...value, color: '#ff3b30' }}>${fmtPrice(s.liquidationPriceUsd)}</div>
            </div>
          </>
        )}
      </div>
      {s.capitalLegs && s.capitalLegs.length > 0 && (
        <div style={{ color: 'var(--tg-hint)', fontSize: 12, lineHeight: 1.6, marginBottom: 10 }}>
          {s.capitalLegs.map((leg, idx) => (
            <div key={`${leg.exchange}-${leg.role}-${idx}`}>
              • {leg.exchange.toUpperCase()} ({leg.role}): {fmtUsd(leg.usd)} ({leg.note})
            </div>
          ))}
        </div>
      )}

      <div style={{ background: 'var(--tg-secondary-bg)', borderRadius: 8, padding: '10px 12px', fontSize: 13, color: 'var(--tg-text)', lineHeight: 1.6 }}>
        {withShort ? (
          <>
            <div style={cell}>
              <b>1.</b> На <b>{s.buyExch.toUpperCase()}</b> BUY {fmtBase(s.qtyBase, base)} опциона по цене ≈{fmtPrice(s.buyAsk)} USDT — premium lock {fmtUsd(s.premiumLockUsd)}.
            </div>
            <div style={cell}>
              <b>2.</b> На <b>{s.sellExch.toUpperCase()}</b> SELL TO OPEN (шорт) {fmtBase(s.qtyBase, base)} того же опциона по цене ≈{fmtPrice(s.sellBid)} USDT — заблокирует margin ≈{fmtUsd(s.marginLockUsd)}.
            </div>
            <div style={cell}>
              <b>3.</b> Net P&L (зафиксирован): <b>{fmtSignedUsd(s.netUsd)}</b> (ROI капитала {fmtPct(s.roiOnCapitalPct)}, APR {fmtPct(s.aprPct, 0)}).
            </div>
            <div style={cell}>
              <b>4.</b> Держать до экспирации ({Math.round(daysToExpiry)}d) или закрыть руками раньше.
            </div>
            <div style={{ padding: '6px 0', color: '#ff3b30' }}>
              ⚠️ <b>Риск ликвидации шорта</b> при движении {base} до <b>${fmtPrice(s.liquidationPriceUsd)}</b>. Следить за margin call.
            </div>
          </>
        ) : (
          <>
            <div style={cell}>
              <b>1.</b> На бирже <b>{s.buyExch.toUpperCase()}</b> разместить ордер BUY на {fmtBase(s.qtyBase, base)} по цене ≈{fmtPrice(s.buyAsk)} USDT (заплатить премию {fmtUsd(s.buyAsk * s.qtyBase)}).
            </div>
            <div style={cell}>
              <b>2.</b> На бирже <b>{s.sellExch.toUpperCase()}</b> немедленно SELL TO CLOSE существующего лонга на {fmtBase(s.qtyBase, base)} по цене ≈{fmtPrice(s.sellBid)} USDT.
            </div>
            <div style={cell}>
              <b>3.</b> Зафиксированный результат: <b>{fmtSignedUsd(s.netUsd)}</b> (ROI премии {fmtPct(s.netPctOfPremium)}).
            </div>
            <div style={{ padding: '6px 0', color: 'var(--tg-hint)', fontSize: 12 }}>
              ⚠️ Предполагается, что у вас <b>уже есть</b> позиция {fmtBase(s.qtyBase, base)} long на {s.sellExch.toUpperCase()}.
            </div>
          </>
        )}
      </div>
      {s.warnings && s.warnings.length > 0 && (
        <div style={{ marginTop: 10, color: '#ff9500', fontSize: 12, lineHeight: 1.5 }}>
          {s.warnings.map((w) => <div key={w}>⚠️ {w}</div>)}
        </div>
      )}
    </div>
  );
}

/** Страница детального просмотра Eta-алерта. */
interface RawQuoteRow { exchange: string; bid: number; ask: number; ageLabel: string }

/** raw алерта: { [exchange]: { bid, ask, takenAt?, ... } } → строки с возрастом котировки. */
function parseRawQuotes(raw: unknown, createdAt: string): RawQuoteRow[] {
  if (!raw || typeof raw !== 'object') return [];
  const created = new Date(createdAt).getTime();
  return Object.entries(raw as Record<string, { bid?: unknown; ask?: unknown; takenAt?: unknown }>)
    .map(([exchange, v]) => {
      const takenMs = typeof v?.takenAt === 'string' ? new Date(v.takenAt).getTime() : NaN;
      const ageMin = Number.isFinite(takenMs) && Number.isFinite(created) ? Math.max(0, (created - takenMs) / 60_000) : null;
      return {
        exchange,
        bid: Number(v?.bid) || 0,
        ask: Number(v?.ask) || 0,
        ageLabel: ageMin == null ? '' : ageMin < 1 ? '<1 мин' : `${Math.round(ageMin)} мин`,
      };
    })
    .sort((a, b) => a.exchange.localeCompare(b.exchange));
}

export function EtaAlertDetailPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [alert, setAlert] = useState<EtaAlert | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    void sb
      .from('eta_alerts')
      .select('*')
      .eq('id', Number(id))
      .maybeSingle()
      .then(({ data, error }) => {
        setLoading(false);
        if (error) {
          console.error('[EtaAlertDetailPage] load error:', error.message);
          return;
        }
        setAlert(data as EtaAlert | null);
      });
  }, [id]);

  if (loading) return <Page title="Eta Alert"><Spinner /></Page>;
  if (!alert) {
    return (
      <Page title="Eta Alert">
        <Empty icon="⚖️" title="Алерт не найден" hint="Проверьте ID или вернитесь назад." />
      </Page>
    );
  }

  const base = extractBase(alert.normalized_key);
  const daysToExpiry = (new Date(alert.expiry).getTime() - Date.now()) / 86_400_000;
  const { noShort: noShortScenario, withShort: withShortScenario, bestNet, bestApr } = resolveEtaScenarios(alert);
  const rawQuotes = parseRawQuotes(alert.raw, alert.created_at);

  const rowStyle: React.CSSProperties = {
    display: 'flex', justifyContent: 'space-between',
    padding: '6px var(--space-lg)',
    borderBottom: '0.5px solid var(--tg-separator)',
    fontSize: 13,
  };

  return (
    <Page title="Eta — Детали алерта">
      <Section header="Информация об опционе">
        <div style={rowStyle}>
          <span style={{ color: 'var(--tg-hint)' }}>Символ</span>
          <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{alert.normalized_key}</span>
        </div>
        <div style={rowStyle}>
          <span style={{ color: 'var(--tg-hint)' }}>Underlying</span>
          <span>{base}</span>
        </div>
        <div style={rowStyle}>
          <span style={{ color: 'var(--tg-hint)' }}>Тип</span>
          <span style={{ fontWeight: 700, color: alert.option_type === 'C' ? '#34c759' : '#ff3b30' }}>
            {alert.option_type === 'C' ? 'Call' : 'Put'}
          </span>
        </div>
        <div style={rowStyle}>
          <span style={{ color: 'var(--tg-hint)' }}>Страйк</span>
          <span>{fmtPrice(alert.strike)} USDT</span>
        </div>
        <div style={rowStyle}>
          <span style={{ color: 'var(--tg-hint)' }}>Экспирация</span>
          <span>{fmtExpiry(alert.expiry)} ({Math.round(Math.max(0, daysToExpiry))}d)</span>
        </div>
        {(alert.cross_spread_pct ?? 0) > 0 && (
          <div style={rowStyle}>
            <span style={{ color: 'var(--tg-hint)' }}>Межбиржевой спред</span>
            <span style={{ color: '#34c759', fontWeight: 700 }}>+{fmtPct(alert.cross_spread_pct ?? 0, 1)}</span>
          </div>
        )}
        {bestNet != null && (
          <div style={rowStyle}>
            <span style={{ color: 'var(--tg-hint)' }}>Best net</span>
            <span style={{ color: bestNet > 0 ? '#34c759' : '#ff3b30', fontWeight: 700 }}>{fmtSignedUsd(bestNet)}</span>
          </div>
        )}
        {bestApr != null && (
          <div style={rowStyle}>
            <span style={{ color: 'var(--tg-hint)' }}>Best APR</span>
            <span style={{ color: '#ff9500', fontWeight: 700 }}>{fmtPct(bestApr, 0)}</span>
          </div>
        )}
      </Section>

      {rawQuotes.length > 0 && (
        <Section header="Котировки по биржам" footer="Возраст — сколько прошло от снимка котировки до создания алерта. Котировки старше 10 минут относительно самой свежей в анализ не попадают.">
          {rawQuotes.map((q) => (
            <div key={q.exchange} style={rowStyle}>
              <span style={{ fontWeight: 600 }}>{q.exchange.toUpperCase()}</span>
              <span style={{ fontSize: 12 }}>
                bid {q.bid > 0 ? fmtPrice(q.bid) : '—'} / ask {q.ask > 0 ? fmtPrice(q.ask) : '—'}
                <span style={{ color: 'var(--tg-hint)', marginLeft: 6 }}>{q.ageLabel}</span>
              </span>
            </div>
          ))}
        </Section>
      )}

      {noShortScenario ? (
        <Section
          header="📊 Сценарий 1: без шорта"
          footer="Закрытие существующего лонга. Предполагается, что позиция уже открыта на sell-бирже."
        >
          <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
            <ScenarioCard scenario={noShortScenario} base={base} daysToExpiry={daysToExpiry} withShort={false} />
          </div>
        </Section>
      ) : (
        <Section header="📊 Сценарий 1: без шорта">
          <div style={{ padding: 'var(--space-md) var(--space-lg)', color: 'var(--tg-hint)', fontSize: 13 }}>
            Не рассчитан: нет доступных bid/ask котировок.
          </div>
        </Section>
      )}

      {withShortScenario ? (
        <Section
          header="🔀 Сценарий 2: со шортом"
          footer="Полный cross-exchange арбитраж. Учитывает margin lock, fees и ROI на капитал."
        >
          <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
            <ScenarioCard scenario={withShortScenario} base={base} daysToExpiry={daysToExpiry} withShort={true} />
          </div>
        </Section>
      ) : (
        <Section header="🔀 Сценарий 2: со шортом">
          <div style={{ padding: 'var(--space-md) var(--space-lg)', color: 'var(--tg-hint)', fontSize: 13 }}>
            Не рассчитан: нет доступных bid/ask котировок.
          </div>
        </Section>
      )}

      <Section header="⚠️ Дисклеймер">
        <div style={{ padding: 'var(--space-md) var(--space-lg)', color: 'var(--tg-hint)', fontSize: 12, lineHeight: 1.6 }}>
          Расчёт по top-of-book quote. Реальная ликвидность L2 не учтена. Latency между биржами может уничтожить спред. Размеры стакана могут отличаться в момент исполнения. Это инструмент мониторинга, не торговый совет.
        </div>
      </Section>

      <div style={{ padding: 'var(--space-md) var(--space-lg)' }}>
        <button
          onClick={() => navigate('/eta')}
          style={{
            background: 'var(--tg-secondary-bg)',
            border: 'none',
            borderRadius: 8,
            padding: '10px 20px',
            color: 'var(--tg-link)',
            fontSize: 14,
            cursor: 'pointer',
            width: '100%',
          }}
        >
          ← Назад к алертам
        </button>
      </div>
    </Page>
  );
}
